import React, { useState, useEffect } from 'react';
import { useLoginState } from '@xingternal/hops';
import * as Types from '@xingternal/pages-graphql-types';

import {
  LAZY_LOADING_TYPE_BUTTON,
  LAZY_LOADING_TYPE_SCROLL,
  useEditContext,
  ModuleHeader,
  usePageContext,
  useMainPageLoadModule,
} from '@xingternal/pages-common';
import { Column, Row } from '@xingternal/xing-grid';

import { getSubPageKey, newsPath, usePageSize } from '../utils';
import News from './News';
import { NewsCommbox } from '../NewsCommbox';
import { EntityPageFeedCollectionFragment } from '../EntityPageFeedCollectionFragment.gql-types';


import styles from './News.css';

const buildSubheader = (
  showHeadline: boolean,
  showSubpageLinks: boolean,
  slug: string,
  subPageKey: string,
  basePath: string
) =>
  showHeadline ? (
    <ModuleHeader
      headlineCopyKey="EP_NEWS_HEADLINE"
      showMore={
        showSubpageLinks
          ? {
              to: newsPath(basePath, slug),
              copyKey: `${subPageKey}`,
            }
          : null
      }
    />
  ) : null;

type NewsContainerProps = {
  moduleProperties?: any;
  isDetailPage?: boolean;
  lazyLoadingType?: typeof LAZY_LOADING_TYPE_BUTTON
    | typeof LAZY_LOADING_TYPE_SCROLL
    | null;
  loadingSkeletonCount?: number;
  showHeadline?: boolean;
  showSubpageLinks?: boolean;
};

const NewsContainer = ({
  moduleProperties: { hasSubpage = true } = {},
  isDetailPage = false,
  lazyLoadingType = !hasSubpage ? LAZY_LOADING_TYPE_BUTTON : null,
  loadingSkeletonCount = 3,
  showHeadline = hasSubpage,
  showSubpageLinks = hasSubpage,
  ...props
}: NewsContainerProps) => {
  useMainPageLoadModule();
  const { isEditing } = useEditContext();
  const { pageContext } = usePageContext();
  const [lazyLoadingIsAllowed, setLazyLoadingIsAllowed] = useState(true);
  const [selectedPosting, setSelectedPosting] = useState<EntityPageFeedCollectionFragment["object"] | null>();
  const [currentPublicationState, setCurrentPublicationState] = useState<Types.PostingPublicationState>(
    Types.PostingPublicationState.Published
  );
  const [hasErrorOnSubpageLabel, setHasErrorOnSubpageLabel] = useState(false);
  const {
    focusType,
    globalId,
    pageId,
    pageSlug,
    pageTitle,
    logo,
  } = pageContext;

  useEffect(() => {
    setLazyLoadingIsAllowed(true);
  }, [currentPublicationState]);

  const clearPosting = () => {
    if (selectedPosting !== null || selectedPosting !== undefined) {
      setSelectedPosting(null);
    }
  };

  useEffect(() => {
    if (!isEditing) setSelectedPosting(null);
  }, [isEditing]);

  const actor = {
    logo,
    title: pageTitle,
    slug: pageSlug,
    isInsider: false,
  };

  showHeadline = !isDetailPage && hasSubpage;
  showSubpageLinks = !isDetailPage && hasSubpage;

  const limit = usePageSize(hasSubpage, useLoginState());
  const canEditLinkPreview = focusType === 'PUBLISHER' && isEditing;
  const defaultPostVisibility = Types.PostingsVisibility.Public;
  const feedQueryVars = {
    id: pageId,
    limit,
    filter: currentPublicationState,
  };

  const subPageKey = getSubPageKey(isEditing);
  const moduleHeader = buildSubheader(
    showHeadline,
    showSubpageLinks && !hasErrorOnSubpageLabel,
    actor.slug,
    subPageKey,
    pageContext?.basePath
  );

  return (
    <>
      {moduleHeader}
      <Row className={styles.row}>
        <Column
          sizeDesktop={10}
          offsetDesktop={1}
          size={12}
          className={styles.col}
        >
          {isEditing && (
            <NewsCommbox
              canEditLinkPreview={canEditLinkPreview}
              clearPosting={clearPosting}
              defaultPostVisibility={defaultPostVisibility}
              feedQueryVars={feedQueryVars}
              selectedPosting={selectedPosting}
              setLazyLoadingIsAllowed={setLazyLoadingIsAllowed}
              setSelectedPosting={setSelectedPosting}
            >
              {(onClick: () => void) => (
                <News
                  actor={actor}
                  feedQueryVars={feedQueryVars}
                  focusType={focusType}
                  globalId={globalId}
                  isDetailPage={isDetailPage}
                  lazyLoadingIsAllowed={lazyLoadingIsAllowed}
                  lazyLoadingType={lazyLoadingType}
                  loadingSkeletonCount={loadingSkeletonCount}
                  onClick={onClick}
                  setCurrentPublicationState={setCurrentPublicationState}
                  setLazyLoadingIsAllowed={setLazyLoadingIsAllowed}
                  setSelectedPosting={setSelectedPosting}
                  showActors={hasSubpage}
                  showSubpageLinks={showSubpageLinks}
                  setHasErrorOnSubpageLabel={setHasErrorOnSubpageLabel}
                  {...props}
                />
              )}
            </NewsCommbox>
          )}
          {!isEditing && (
            <News
              actor={actor}
              feedQueryVars={feedQueryVars}
              focusType={focusType}
              globalId={globalId}
              isDetailPage={isDetailPage}
              lazyLoadingIsAllowed={lazyLoadingIsAllowed}
              lazyLoadingType={lazyLoadingType}
              loadingSkeletonCount={loadingSkeletonCount}
              setCurrentPublicationState={setCurrentPublicationState}
              setLazyLoadingIsAllowed={setLazyLoadingIsAllowed}
              showActors={hasSubpage}
              showSubpageLinks={showSubpageLinks}
              setHasErrorOnSubpageLabel={setHasErrorOnSubpageLabel}
              {...props}
            />
          )}
        </Column>
      </Row>
    </>
  );
};

export default NewsContainer;
